import {
  createStyles,
  Timeline,
  Text,
  Stack,
  TimelineProps,
} from '@mantine/core';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React from 'react';

dayjs.extend(relativeTime);

interface InvoiceTimelineProps {
  invoiceCreatedAt: string;
  purchaseOrderFile: string | null;
  purchaseOrderFileUploadedAt: string | null;
  purchaseOrderApprovedAt: string | null;
  purchaseOrderApprovedBy: string | null;
  receiptFile: string | null;
  receiptFileUploadedAt: string | null;
  receiptFileUploadedBy: string | null;
  isPaymentReceive: boolean | null;
  paymentReceivedAt: string | null;
}

export function InvoiceTimeline({
  invoiceCreatedAt,
  purchaseOrderFile,
  purchaseOrderFileUploadedAt,
  purchaseOrderApprovedAt,
  purchaseOrderApprovedBy,
  receiptFile,
  receiptFileUploadedAt,
  receiptFileUploadedBy,
  paymentReceivedAt,
  isPaymentReceive,
}: InvoiceTimelineProps) {
  const { classes } = useStyles();

  const getTimelineItemContent = (date: string | null, by?: string | null) => {
    const time = dayjs(date).format('MMM D, YYYY h:mm');

    if (!date) return null;

    return (
      <Text size="xs" color="gray.5">
        <Text span>{time} </Text>
        {by && <Text span>by {by}</Text>}
      </Text>
    );
  };

  return (
    <Stack className={classes.container} spacing="xl">
      <Text size="md" weight={600}>
        Timeline
      </Text>

      <Timeline
        bulletSize={14}
        color="gray"
        lineWidth={2}
        styles={timelineStyles}
      >
        {invoiceCreatedAt && (
          <Timeline.Item title="Invoice generated">
            {getTimelineItemContent(invoiceCreatedAt)}
          </Timeline.Item>
        )}

        {purchaseOrderFile && (
          <Timeline.Item title="Purchase order uploaded">
            {getTimelineItemContent(purchaseOrderFileUploadedAt)}
          </Timeline.Item>
        )}

        {purchaseOrderApprovedBy && (
          <Timeline.Item title="Purchase order approved">
            {getTimelineItemContent(
              purchaseOrderApprovedAt,
              purchaseOrderApprovedBy
            )}
          </Timeline.Item>
        )}

        {isPaymentReceive && (
          <Timeline.Item title="Payment received">
            {getTimelineItemContent(paymentReceivedAt)}
          </Timeline.Item>
        )}

        {receiptFile && (
          <Timeline.Item title="Receipt file uploaded">
            {getTimelineItemContent(
              receiptFileUploadedAt,
              receiptFileUploadedBy
            )}
          </Timeline.Item>
        )}
      </Timeline>
    </Stack>
  );
}

const timelineStyles: TimelineProps['styles'] = (theme) => ({
  itemTitle: {
    fontWeight: 400,
    color: theme.colors.gray[8],
  },
});

const useStyles = createStyles((theme) => ({
  container: {
    backgroundColor: theme.fn.rgba(theme.colors.blue_gray[0], 0.3),
    borderRadius: 20,
    padding: theme.spacing['xxl'],
  },
}));
